import { useState } from "react";
import { Link } from "react-router-dom";
import { Lock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

export default function Checkout() {
  const { items, subtotal } = useCart();
  const { user } = useAuth();
  const shipping = subtotal >= 75 || subtotal === 0 ? 0 : 6;
  const total = subtotal + shipping;

  const [form, setForm] = useState({ name: user?.name || "", email: user?.email || "" });
  const [busy, setBusy] = useState(false);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const pay = async (e) => {
    e.preventDefault();
    if (items.length === 0) return;
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        items: items.map((i) => ({ slug: i.slug, quantity: i.quantity })),
        origin_url: window.location.origin,
        customer: { name: form.name, email: form.email },
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Checkout failed");
      setBusy(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="grid min-h-[60vh] place-items-center text-center">
        <div>
          <p className="font-serif text-4xl text-ink">Your bag is empty</p>
          <Link to="/shop" className="mt-6 inline-block eyebrow link-underline">Shop Jewelry</Link>
        </div>
      </div>
    );
  }

  const input =
    "w-full rounded-xl border border-sand bg-ivory px-4 py-3.5 text-sm outline-none transition-colors placeholder:text-ink/40 focus:border-ink";

  return (
    <div className="min-h-screen bg-ivory px-5 py-16 md:px-10 md:py-24" data-testid="checkout-page">
      <div className="mx-auto grid max-w-[1100px] gap-14 lg:grid-cols-[1.2fr_1fr]">
        <div>
          <h1 className="font-serif text-4xl font-light tracking-tight text-ink md:text-6xl">Checkout</h1>
          {!user && (
            <p className="mt-3 text-sm text-ink/60">
              Have an account?{" "}
              <Link to="/login" className="link-underline text-ink">Log in</Link> for faster checkout.
            </p>
          )}

          <form onSubmit={pay} className="mt-10 space-y-8" data-testid="checkout-form">
            <div>
              <h2 className="eyebrow mb-4 text-ink/60">Contact Details</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                <input required placeholder="Full name" value={form.name} onChange={upd("name")} className={input} data-testid="checkout-name" />
                <input required type="email" placeholder="Email address" value={form.email} onChange={upd("email")} className={input} data-testid="checkout-email" />
              </div>
            </div>

            <div className="rounded-2xl border border-sand bg-cream p-6">
              <div className="flex items-center gap-2 text-sm text-ink">
                <ShieldCheck size={18} strokeWidth={1.5} className="text-gold" />
                Secure payment powered by Stripe
              </div>
              <p className="mt-3 text-xs leading-relaxed text-ink/60">
                You'll be redirected to Stripe's secure checkout to enter your shipping address and
                card details. This is a test store — use card <span className="text-ink">4242 4242 4242 4242</span>,
                any future expiry and any CVC.
              </p>
            </div>

            <button
              type="submit"
              disabled={busy}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-ink py-4 text-[0.72rem] uppercase tracking-[0.24em] text-ivory transition-colors hover:bg-gold disabled:opacity-60"
              data-testid="pay-with-stripe"
            >
              <Lock size={14} strokeWidth={2} />
              {busy ? "Redirecting…" : `Pay Securely · $${total.toFixed(2)}`}
            </button>
          </form>
        </div>

        {/* Summary */}
        <div className="h-fit rounded-2xl bg-cream p-8 lg:sticky lg:top-32">
          <h2 className="font-serif text-2xl text-ink">Order Summary</h2>
          <ul className="mt-6 space-y-4">
            {items.map((i) => (
              <li key={i.slug} className="flex gap-4">
                <div className="relative">
                  <img src={i.image} alt={i.name} className="h-16 w-14 rounded-lg object-cover" />
                  <span className="absolute -right-2 -top-2 grid h-5 w-5 place-items-center rounded-full bg-ink text-[0.6rem] text-ivory">
                    {i.quantity}
                  </span>
                </div>
                <div className="flex flex-1 items-center justify-between">
                  <span className="font-serif text-lg text-ink">{i.name}</span>
                  <span className="text-sm text-ink/70">${(i.price * i.quantity).toFixed(0)}</span>
                </div>
              </li>
            ))}
          </ul>
          <div className="mt-6 space-y-2 border-t border-black/10 pt-4 text-sm text-ink/70">
            <div className="flex justify-between"><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
            <div className="flex justify-between"><span>Shipping</span><span>{shipping === 0 ? "Free" : `$${shipping.toFixed(2)}`}</span></div>
            <div className="flex justify-between text-xs text-ink/50"><span>Tax</span><span>Calculated at payment</span></div>
            <div className="flex justify-between border-t border-black/10 pt-3 font-serif text-2xl text-ink">
              <span>Total</span><span>${total.toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
